import * as React from 'react'
import type {
  DiffOperationSummary,
  DiffPropertyChange,
  DiffUploadResponse
} from '@axilon-apps/domains-ontology-ui-state'
import {
  CollapsedSection,
  CountChips,
  DIFF_TABLE_PAGE_SIZE,
  DiffPagerRow,
  DiffRow,
  DiffTable,
  KindBadge,
  PropertyDiffTable,
  previewDiffValue,
  type CountChip,
  type DiffTone
} from '../../../components/DiffTable'

const UNIT_SEP = '\x1F'

function displayEntityId(id: string | number): string {
  return String(id).replaceAll(UNIT_SEP, '/')
}

const KIND_TONE: Record<'create' | 'modify' | 'delete', DiffTone> = {
  create: 'add',
  modify: 'change',
  delete: 'remove'
}

interface DiffResultViewProps {
  result: DiffUploadResponse
}

export function DiffResultView({ result }: DiffResultViewProps) {
  // Partition into "primary" (creates, structural modifies, deletes — the
  // things the user actually cares about reviewing) and "value-only" (gateway
  // runtime memory-tag drift, hidden behind a toggle so it doesn't drown out
  // real changes). Memoized so paging below doesn't re-partition — and so the
  // page-reset effect keyed on it only fires for a genuinely new result.
  const { primaryOps, valueOnlyOps, counts } = React.useMemo(() => {
    const primaryOps: DiffOperationSummary[] = []
    const valueOnlyOps: DiffOperationSummary[] = []
    const counts = {
      create: 0,
      structuralModify: 0,
      valueOnlyModify: 0,
      delete: 0
    }
    for (const op of result.operations) {
      const isValueOnly = op.kind === 'modify' && op.change_kind === 'value-only'
      if (isValueOnly) {
        valueOnlyOps.push(op)
        counts.valueOnlyModify += 1
      } else {
        primaryOps.push(op)
        if (op.kind === 'create') counts.create += 1
        else if (op.kind === 'delete') counts.delete += 1
        else if (op.kind === 'modify') counts.structuralModify += 1
      }
    }
    return { primaryOps, valueOnlyOps, counts }
  }, [result])

  const [primaryPage, setPrimaryPage] = React.useState(0)
  const [valueOnlyPage, setValueOnlyPage] = React.useState(0)
  // A fresh diff result starts both tables from the first page.
  React.useEffect(() => {
    setPrimaryPage(0)
    setValueOnlyPage(0)
  }, [result])

  if (result.rolled_back) {
    return (
      <div className="flex flex-col gap-3">
        <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down">
          Diff apply failed — {result.errors.length} error
          {result.errors.length !== 1 ? 's' : ''} encountered. Graph has been rolled back to its
          previous state.
        </div>
        {result.errors.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <div className="text-xs font-semibold text-st-down">
              Errors ({result.errors.length})
            </div>
            {result.errors.map((err, index) => (
              <div
                key={`err-${index}`}
                role="alert"
                className="rounded-md bg-st-down-bg px-3 py-1.5 text-xs text-st-down"
              >
                <strong>
                  {err.entity_type} [{displayEntityId(err.entity_id)}]:
                </strong>{' '}
                {err.message}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const clampPage = (page: number, total: number) =>
    Math.min(page, Math.max(1, Math.ceil(total / DIFF_TABLE_PAGE_SIZE)) - 1)
  const safePrimaryPage = clampPage(primaryPage, primaryOps.length)
  const primaryStart = safePrimaryPage * DIFF_TABLE_PAGE_SIZE
  const shownPrimary = primaryOps.slice(primaryStart, primaryStart + DIFF_TABLE_PAGE_SIZE)
  const safeValueOnlyPage = clampPage(valueOnlyPage, valueOnlyOps.length)
  const valueOnlyStart = safeValueOnlyPage * DIFF_TABLE_PAGE_SIZE
  const shownValueOnly = valueOnlyOps.slice(valueOnlyStart, valueOnlyStart + DIFF_TABLE_PAGE_SIZE)

  const chips: CountChip[] = []
  if (counts.create > 0) {
    chips.push({ text: `${counts.create.toLocaleString()} create`, tone: 'add' })
  }
  if (counts.structuralModify > 0) {
    chips.push({
      text: `${counts.structuralModify.toLocaleString()} structural change${
        counts.structuralModify !== 1 ? 's' : ''
      }`,
      tone: 'change'
    })
  }
  if (counts.delete > 0) {
    chips.push({ text: `${counts.delete.toLocaleString()} delete`, tone: 'remove' })
  }
  if (counts.valueOnlyModify > 0) {
    chips.push({
      text: `${counts.valueOnlyModify.toLocaleString()} value-only`,
      tone: 'neutral',
      title: 'These are runtime memory-tag value updates from the gateway, not config changes.'
    })
  }

  return (
    <div className="flex flex-col gap-3">
      {result.operations.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <div className="text-11 font-semibold tracking-wider uppercase text-ink-3">
              Operations ({result.operations.length.toLocaleString()})
            </div>
            <CountChips chips={chips} />
          </div>
          {primaryOps.length > 0 && (
            <DiffTable>
              {/* Keys carry the absolute index so a row's uncontrolled
                  expansion state never bleeds onto another page's row. */}
              {shownPrimary.map((op, index) => (
                <DiffOperationRow key={`${op.urn}-${primaryStart + index}`} op={op} />
              ))}
              <DiffPagerRow
                page={safePrimaryPage}
                total={primaryOps.length}
                onPageChange={setPrimaryPage}
              />
            </DiffTable>
          )}
          {valueOnlyOps.length > 0 && (
            <CollapsedSection
              title={`${valueOnlyOps.length.toLocaleString()} value-only change${
                valueOnlyOps.length !== 1 ? 's' : ''
              }`}
              hint="gateway memory-tag value drift — not config changes"
            >
              <DiffTable bare>
                {shownValueOnly.map((op, index) => (
                  <DiffOperationRow key={`vo-${op.urn}-${valueOnlyStart + index}`} op={op} />
                ))}
                <DiffPagerRow
                  page={safeValueOnlyPage}
                  total={valueOnlyOps.length}
                  onPageChange={setValueOnlyPage}
                />
              </DiffTable>
            </CollapsedSection>
          )}
        </div>
      )}

      {result.errors.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            {result.preview ? 'Preview' : 'Apply'} encountered {result.errors.length} error
            {result.errors.length !== 1 ? 's' : ''} — these entities will fail during apply.
          </div>
          {result.errors.map((err, index) => (
            <div
              key={`err-${index}`}
              role="alert"
              className="rounded-md bg-st-down-bg px-3 py-1.5 text-xs text-st-down"
            >
              <strong>
                {err.entity_type} [{displayEntityId(err.entity_id)}]:
              </strong>{' '}
              {err.message}
            </div>
          ))}
        </div>
      )}

      {!result.preview && result.errors.length === 0 && (
        <div role="status" className="rounded-md bg-st-run-bg px-3 py-2 text-xs text-st-run">
          Diff applied successfully — {result.operations.length} operation
          {result.operations.length !== 1 ? 's' : ''} completed with no errors.
        </div>
      )}
    </div>
  )
}

function DiffOperationRow({ op }: { op: DiffOperationSummary }) {
  const changes = op.kind === 'modify' && op.changes != null ? Object.entries(op.changes) : []
  const changeCount = changes.length

  return (
    <DiffRow
      badge={<KindBadge tone={KIND_TONE[op.kind]}>{op.kind}</KindBadge>}
      type={op.entity_type}
      entity={displayEntityId(op.entity_id)}
      entityTitle={op.urn}
      summary={
        changeCount > 0 ? `(${changeCount} field${changeCount !== 1 ? 's' : ''})` : undefined
      }
    >
      {changeCount > 0 ? (
        <PropertyDiffTable
          columns="before-after"
          beforeHeader="Old"
          afterHeader="New"
          rows={changes.map(([path, change]) => propertyRow(path, change))}
        />
      ) : undefined}
    </DiffRow>
  )
}

function propertyRow(path: string, change: DiffPropertyChange) {
  return {
    name: path,
    before: change.patch != null ? <em>text patch</em> : previewDiffValue(change.oldValue),
    after: change.patch != null ? <em>text patch</em> : previewDiffValue(change.newValue),
    beforeTitle: String(change.oldValue ?? ''),
    afterTitle: String(change.newValue ?? '')
  }
}
